import { Answers } from '@polylearn/models';
import { keys, mean, sortBy, takeRight } from 'lodash';
import { groupBy, mapObject, max, min } from 'underscore';

const subCategoryMinExtraData = {
  'Compréhension de l’oral': {
    minAnswered: 9,
    shouldConsidereFeedbackScore: false,
    lastAnswer: 90
  },
  'Compréhension des écrits': {
    minAnswered: 9,
    shouldConsidereFeedbackScore: false,
    lastAnswer: 90
  },
  'Production orale': {
    minAnswered: 2,
    shouldConsidereFeedbackScore: true,
    lastAnswer: 8
  },
  'Production écrite': {
    minAnswered: 2,
    shouldConsidereFeedbackScore: true,
    lastAnswer: 8
  }
};

export const getAverageByPartsDelf = (answers: Answers[], parts: any[]) => {
  let answersWithSubCompetence = answers
    .map(answer => {
      let correspondingPart = parts.find(part => part.id === answer.part);

      if (!correspondingPart) {
        return null;
      }

      let subCompetence = correspondingPart.subCategory;

      return {
        ...answer,
        subCompetence,
        maxScore: correspondingPart ? correspondingPart.maxScore : null
      };
    })
    .filter(answer => answer !== null);

  let answersByPart = groupBy(answersWithSubCompetence, 'part');

  let res = mapObject(answersByPart, (subAnswers, partId) => {
    // double equal because number != string
    let part = parts.find(p => p.id == partId);

    let subCompetence = part.subCategory;

    let extraData = subCategoryMinExtraData[subCompetence];

    let percentage = calculatePercentage(
      subAnswers,
      extraData?.minAnswered,
      extraData?.shouldConsidereFeedbackScore,
      extraData?.lastAnswer
    );

    return percentage;
  });

  return res;
};

const calculatePercentage = (
  answers: Answers[],
  minToBeAnswered = 0,
  shouldConsidereFeedbackScore = false,
  lastAnswerThreshold = null
): null | number => {
  /*
  console.log(
    '🔥 Calcul du pourcentage avec:',
    answers.length,
    minToBeAnswered,
    shouldConsidereFeedbackScore
  );
  */
  let filteredAnswers = shouldConsidereFeedbackScore
    ? answers.filter(
        a => a.feedback_score >= 0 && a.extra_value && a.question?.value > 0
      )
    : answers;

  if (lastAnswerThreshold !== null) {
    let sortedAnswers = sortBy(filteredAnswers, 'created_at');
    filteredAnswers = takeRight(sortedAnswers, lastAnswerThreshold);
  }

  /*
  console.log('🔥 Réponses filtrées:', filteredAnswers.length);
  */

  if (filteredAnswers.length < minToBeAnswered) {
    return null;
  }

  if (shouldConsidereFeedbackScore) {
    let totalFeedbackScore = filteredAnswers.reduce(
      (sum, a) => sum + a.feedback_score,
      0
    );

    let maxTotalFeedScore = filteredAnswers.reduce(
      (sum, a) => sum + (a.question?.value || 0),
      0
    );

    let score = min([totalFeedbackScore / maxTotalFeedScore, 1]);

    return score;
  }

  return (
    filteredAnswers.filter(a => a.is_correct).length / filteredAnswers.length
  );
};

export const calculatePartsLevelsDelf = (
  partsLevel: Record<string, number | null>,
  parts: any[]
): Record<string, number | null> => {
  const partLevels = keys(partsLevel).map(partId => {
    // double equal because number != string
    const part = parts.find(p => p.id == partId);
    const subCompetence = part.subCategory;
    return {
      subCompetence,
      score: partsLevel[partId],
      partId: partId
    };
  });

  // console.log('🔥 partLevels:', partLevels);

  const groupByCompetence = groupBy(partLevels, 'subCompetence');
  // console.log('🔥 groupByCompetence:', groupByCompetence);

  return mapObject(groupByCompetence, (values, competence) => {
    const validScores = values
      .map(v => v.score)
      .filter(score => score !== null) as number[];
    if (validScores.length === 0) {
      return null;
    }

    // console.log('🔥 groupByCompetence:', groupByCompetence);

    return mean(validScores);
  });
};

export const calculateMeanOfTheObject = (
  obj: Record<string, number | null>
) => {
  const values = Object.values(obj);
  const valuesFiltered = values.filter(v => v >= 0 && v !== null);
  const sum = valuesFiltered.reduce((acc, val) => acc + val, 0);
  return sum / valuesFiltered.length;
};

export const getSectionsByLevel = (sections: any[], level) => {
  return sections.map(section => {
    return {
      ...section,
      parts: section.parts.filter(part => {
        if (!part.level) {
          return true;
        }

        if (!level) {
          return true;
        }

        if (part.level === level) {
          return true;
        }
      })
    };
  });
};
